import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { invokeEdgeFunction } from '../lib/edgeFunctions';
import { createLogger } from '../lib/logger';
import { useAuth } from './AuthContext';

const logger = createLogger('Credits');

interface CreditBalance {
  balance: number;
  currency: string;
}

interface CreditsContextType {
  balance: number;
  loading: boolean;
  refreshBalance: () => Promise<void>;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  const organisationId = profile?.organisation_id;

  const refreshBalance = useCallback(async () => {
    if (!organisationId) {
      setLoading(false);
      return;
    }
    try {
      const result = await invokeEdgeFunction<CreditBalance>('credit-operations', {
        action: 'get_balance',
      });
      setBalance(result.balance ?? 0);
    } catch (err) {
      logger.warn('Failed to fetch credit balance', err);
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  useEffect(() => {
    // Pages dispatch new Event('credits-updated') after deducts/top-ups.
    const handler = () => {
      refreshBalance();
    };
    window.addEventListener('credits-updated', handler);
    return () => window.removeEventListener('credits-updated', handler);
  }, [refreshBalance]);

  return (
    <CreditsContext.Provider value={{ balance, loading, refreshBalance }}>
      {children}
    </CreditsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCredits(): CreditsContextType {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}
